import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Network,
  ShareContentInput,
  useGetContentQuery,
  useGetSharePreviewInfoQuery,
  useShareContentMutation,
} from 'api';
import { useProtectedClient, useFacebookRedirect } from 'hooks';
import { ContentProvider, SharePhotoProvider } from 'providers';

import { PartnerShare, PartnerShareLoader } from '../../components';
import { Share } from '../../types';
import {
  addNetwork,
  partnerRedirect,
  partnerRedirectError,
  prepShareItems,
  removeNetwork,
} from '../../helpers';

interface Props {
  redirectUrl: string;
  partnerName: string;
}

const PartnerShareProtected: FC<Props> = ({ partnerName, redirectUrl }) => {
  const client = useProtectedClient();
  const routeParams = useParams();
  const { redirectToFacebook, facebookStatus } = useFacebookRedirect();
  const [isSharingToFacebook, setIsSharingToFacebook] = useState(false);

  const { content_id } = routeParams;

  // Back from Facebook. Handle the result
  useEffect(() => {
    if (!facebookStatus) {
      return;
    }

    if (facebookStatus === 'canceled') {
      removeNetwork(Network.Facebook);
    }

    if (facebookStatus === 'success') {
      addNetwork(Network.Facebook);
    }

    partnerRedirect(redirectUrl);
  }, [facebookStatus]);

  // Content Query
  const { data: contentData, isLoading: isContentLoading } = useGetContentQuery(
    { content_id },
    {
      onError: () => partnerRedirectError(redirectUrl, 'Error when fetching content'),
    }
  );

  // SharePreviewInfo Query
  const { data: sharePreviewData, isLoading: isSharePreviewLoading } = useGetSharePreviewInfoQuery(
    { content_id },
    {
      onError: () => partnerRedirectError(redirectUrl, 'Error when calling getSharePreviewInfo'),
    }
  );

  // Share Content Mutation
  const { mutateAsync: shareContent } = useShareContentMutation({
    onError: () => partnerRedirectError(redirectUrl, 'Error when calling shareContent'),
    onSuccess: handleShareComplete,
  });

  function handleShareComplete() {
    const shareUrl =
      sharePreviewData.getSharePreviewInfo.shortened_urls?.facebook?.short_link ??
      `https://${client.subdomain}/content/${content_id}`;

    isSharingToFacebook ? redirectToFacebook(shareUrl) : partnerRedirect(redirectUrl);
  }

  async function share(formValues: Share) {
    const { items, hasFacebook } = prepShareItems(formValues);

    if (!items.length) {
      return;
    }

    if (hasFacebook) {
      setIsSharingToFacebook(true);
    }

    await shareContent({
      content_id,
      timezone: formValues.timezone,
      items: items as ShareContentInput[],
    });
  }

  async function handleSubmit(formValues: Share) {
    try {
      await share(formValues);
    } catch (error) {
      partnerRedirectError(redirectUrl, 'Unknown error occurred when sharing');
    }
  }

  if (!content_id) {
    partnerRedirectError(redirectUrl, 'content_id is required for partner sharing');
    return null;
  }

  if (isContentLoading || isSharePreviewLoading) {
    return <PartnerShareLoader />;
  }

  if (!contentData?.getContent) {
    partnerRedirectError(redirectUrl, 'Requested content_id not found');
    return null;
  }

  return (
    <ContentProvider value={contentData.getContent}>
      <SharePhotoProvider>
        <PartnerShare client={client} partnerName={partnerName} onSubmit={handleSubmit} />
      </SharePhotoProvider>
    </ContentProvider>
  );
};

export default PartnerShareProtected;
